import { Component, input, inject, computed, effect, signal, ChangeDetectionStrategy } from '@angular/core';
import { NoticeSignal, TabSignal, TabSignalSet } from '@xanadu/signal';
import { NodeModel, Tab } from '@xanadu/model';
import { firstValueFrom } from 'rxjs';
import { Location } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { DataService } from '@xanadu/service';
import { Node } from '@xanadu/model';

@Component({
    selector: 'panel',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class PanelComponent<T extends Node> {
    protected readonly location = inject(Location);
    protected readonly dataService = inject(DataService);
    protected readonly tabSignal = inject(TabSignal);

    protected readonly noticeSignal = inject(NoticeSignal);

    readonly model = input<any>(undefined);

    readonly node = computed<T | undefined>(() => this.model()?.node as T);

    tabs?: Tab[];
    tabSelected?: Tab;

    formGroup = signal<FormGroup | undefined>(undefined);

    get disabled(): boolean {
        const node = this.node();
        return !this.formGroup()?.valid || !this.formGroup()?.dirty && (!node?.isDirty && (!node?.original || node?.name === node?.original) || !node?.name);
    }

    protected type?: string;

    protected get nodeType(): string | undefined {
        return undefined;
    }

    constructor() {
        effect(() => {
            const node = this.node();

            if (node) {
                this.setup(node);
            }
        });

        effect(() => {
            const nodeModel = this.tabSignal.node[this.nodeType as keyof TabSignalSet<NodeModel>]();
            const node = this.node();
            const formGroup = this.formGroup();

            if (nodeModel && node && formGroup && nodeModel.node._id !== node._id) {
                if (formGroup.dirty || node.original && node.original != node.name) {
                    this.extract(node, formGroup.value);
                    if (node && !node.isDirty) {
                        queueMicrotask(() => {
                            node.isDirty = true;
                        });
                    }
                }
            }
        });

        effect(() => {
            const nodeId = this.tabSignal.erase[this.nodeType as keyof TabSignalSet<NodeModel>]();
            const node = this.node();

            if (!!nodeId && nodeId === node?._id) {
                this.formGroup.set(undefined);
            }
        });
    }

    onSubmit() {
        const node = this.node();
        const formGroup = this.formGroup();

        if (node && formGroup) {
            // Extract the form values including disabled controls
            const form = formGroup.getRawValue();
            const error = this.load(node, form);

            if (error?.length) {
                this.noticeSignal.error(error);
            } else {
                void this.save(node);
            }
        }
    }

    onClick(tab: any): void {
        this.tabSelected = tab;
    }

    protected setup(node: T): void {
        if (node) {
            const type = node ? node.constructor as typeof Node : undefined;
            this.type = type?._type;
            this.formGroup.set(new FormGroup({}));
        }
    }

    protected load(node: T, form: any): string | undefined {
        return;
    }

    protected refresh(node: T): void {
        if (node.parent) {
            node.parent.canDelete = false;
        }

        const nodeType = this.nodeType;

        if (nodeType) {
            this.tabSignal.reset[nodeType as keyof TabSignalSet<NodeModel>].set(node);
        }

        this.noticeSignal.info(`${this.type} ${node.name} is successfully refreshed.`);

        this.formGroup()?.markAsPristine();
    }

    protected navigate(node: T): void {
        const nodeType = this.nodeType;
        if (node._id?.length && nodeType) {
            this.location.go(`${nodeType.toLowerCase()}/id/${node._id}`);
        }
    }

    protected async save(node: T): Promise<void> {
        if (node) {
            await firstValueFrom(this.dataService.save(node))
                .then(node => {
                    this.refresh(node);
                    this.navigate(node);
                });
        }
    }

    protected extract(node: T, form: any): void {
        this.load(node, form);
    }
}
